import React, { Component } from 'react';
import { connect } from 'react-redux';
import Editor from '@monaco-editor/react';
import { post_api, get_api } from '../../../redux/api_funcs';
import * as actions from '../../../redux/action_creators';
import { get_api_url } from '../../../utils/urls';

const HISTORY_KEY = 'query_studio_history';
const MAX_HISTORY = 20;
const RULES_DISMISSED_KEY = 'query_studio_rules_dismissed';

const BADGE_STYLES = {
  SELECT: { background: '#dbeafe', color: '#1d4ed8' },
  INSERT: { background: '#dcfce7', color: '#15803d' },
  UPDATE: { background: '#fef3c7', color: '#b45309' },
  DELETE: { background: '#fee2e2', color: '#b91c1c' },
  ALTER: { background: '#ede9fe', color: '#6d28d9' },
  DROP: { background: '#fee2e2', color: '#b91c1c' },
  CREATE: { background: '#cffafe', color: '#0e7490' },
  TRUNCATE: { background: '#fee2e2', color: '#b91c1c' }
};

const DDL_TYPES = new Set(['ALTER', 'DROP', 'CREATE', 'TRUNCATE']);

function detect_statement_type(sql) {
  return sql.trim().split(/\s+/)[0].toUpperCase();
}

function relative_time(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

class DbConsole extends Component {
  state = {
    activeTab: 'editor',
    sql: '',
    dryRunResult: null,
    executeResult: null,
    dryRunError: null,
    executeError: null,
    loadingDryRun: false,
    loadingExecute: false,
    highImpactConfirmed: false,
    dryRunDone: false,
    history: [],
    showHistory: false,
    accessAllowed: null,
    auditLogs: [],
    loadingAuditLogs: false,
    auditOffset: 0,
    hasMoreAuditLogs: true,
    rulesDismissed: localStorage.getItem(RULES_DISMISSED_KEY) === 'true'
  };

  componentDidMount() {
    const history = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
    this.setState({ history });
    this.checkAccess();
  }

  checkAccess = () => {
    get_api(get_api_url('query_studio/check_access'), true)
      .then((resp) => this.setState({ accessAllowed: resp.data.has_access }))
      .catch(() => this.setState({ accessAllowed: false }));
  };

  saveToHistory = (sql) => {
    const { history } = this.state;
    const updated = [sql, ...history.filter((q) => q !== sql)].slice(0, MAX_HISTORY);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
    this.setState({ history: updated });
  };

  handleSqlChange = (value) => {
    this.setState({
      sql: value || '',
      dryRunResult: null,
      executeResult: null,
      dryRunError: null,
      executeError: null,
      dryRunDone: false,
      highImpactConfirmed: false
    });
  };

  runDryRun = () => {
    const { sql } = this.state;
    if (!sql.trim()) return;
    this.setState({
      loadingDryRun: true,
      dryRunResult: null,
      dryRunError: null,
      executeResult: null,
      executeError: null,
      dryRunDone: false,
      highImpactConfirmed: false
    });
    post_api(get_api_url('query_studio/dry_run'), { sql }, true)
      .then((resp) => {
        this.saveToHistory(sql.trim());
        this.setState({ dryRunResult: resp.data.data, loadingDryRun: false, dryRunDone: true });
      })
      .catch((e) => {
        const msg = e?.response?.data?.message || 'Dry run failed.';
        this.setState({ dryRunError: msg, loadingDryRun: false });
      });
  };

  runExecute = () => {
    const { sql } = this.state;
    if (!sql.trim() || !this.canExecute()) return;
    this.setState({ loadingExecute: true, executeResult: null, executeError: null });
    post_api(get_api_url('query_studio/execute'), { sql }, true)
      .then((resp) => {
        this.saveToHistory(sql.trim());
        this.setState({ executeResult: resp.data.data, loadingExecute: false });
        this.props.set_notification_variable(
          true,
          'success',
          `Executed. Rows affected: ${resp.data.data.rowsAffected}`
        );
      })
      .catch((e) => {
        const msg = e?.response?.data?.message || 'Execution failed.';
        this.setState({ executeError: msg, loadingExecute: false });
        this.props.set_notification_variable(true, 'error', msg);
      });
  };

  canExecute = () => {
    const { dryRunDone, dryRunResult, highImpactConfirmed } = this.state;
    if (!dryRunDone) return false;
    if (dryRunResult?.warning && !highImpactConfirmed) return false;
    return true;
  };

  loadAuditLogs = (reset = false) => {
    const offset = reset ? 0 : this.state.auditOffset;
    this.setState({ loadingAuditLogs: true });
    get_api(get_api_url(`query_studio/audit_log?limit=20&offset=${offset}`), true)
      .then((resp) => {
        const newLogs = resp.data.data;
        this.setState((prev) => ({
          auditLogs: reset ? newLogs : [...prev.auditLogs, ...newLogs],
          auditOffset: offset + newLogs.length,
          hasMoreAuditLogs: newLogs.length === 20,
          loadingAuditLogs: false
        }));
      })
      .catch(() => this.setState({ loadingAuditLogs: false }));
  };

  switchTab = (tab) => {
    this.setState({ activeTab: tab });
    if (tab === 'audit' && this.state.auditLogs.length === 0) {
      this.loadAuditLogs(true);
    }
  };

  render_badge = (type) => {
    const s = BADGE_STYLES[type] || { background: '#f1f5f9', color: '#475569' };
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.06em',
          background: s.background,
          color: s.color,
          flexShrink: 0
        }}
      >
        {type}
      </span>
    );
  };

  render_results_table = (rows) => {
    if (!rows || rows.length === 0) {
      return (
        <div style={{ padding: '32px', textAlign: 'center', color: '#cbd5e1', fontSize: '13px' }}>
          No rows returned.
        </div>
      );
    }
    const cols = Object.keys(rows[0]);
    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '12.5px' }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {cols.map((c) => (
                <th
                  key={c}
                  style={{
                    padding: '8px 12px',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: '#64748b',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '2px solid #e2e8f0',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                {cols.map((c) => (
                  <td
                    key={c}
                    style={{
                      padding: '7px 12px',
                      borderBottom: '1px solid #f1f5f9',
                      maxWidth: '260px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: row[c] === null ? '#cbd5e1' : '#334155',
                      fontStyle: row[c] === null ? 'italic' : 'normal'
                    }}
                  >
                    {row[c] === null ? 'NULL' : String(row[c])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  render_editor_view = () => {
    const {
      sql,
      loadingDryRun,
      loadingExecute,
      dryRunResult,
      dryRunError,
      executeResult,
      executeError,
      highImpactConfirmed,
      dryRunDone,
      history,
      showHistory,
      rulesDismissed
    } = this.state;
    const canExec = this.canExecute();

    const displayRows =
      executeResult?.rows?.length > 0
        ? executeResult.rows
        : dryRunResult?.previewRows?.length > 0
          ? dryRunResult.previewRows
          : null;

    const rowCount = executeResult
      ? executeResult.rowsAffected
      : dryRunResult
        ? dryRunResult.rowsAffected
        : null;

    const resultStatement = executeResult?.statementType || dryRunResult?.statementType;
    const isExecuted = !!executeResult;

    return (
      <>
        {/* Editor toolbar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 16px',
            background: '#f8fafc',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', position: 'relative' }}>
            {history.length > 0 && (
              <>
                <button
                  onClick={() => this.setState({ showHistory: !showHistory })}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    background: 'none',
                    border: '1px solid #e2e8f0',
                    borderRadius: '5px',
                    padding: '4px 10px',
                    fontSize: '12px',
                    color: '#64748b',
                    cursor: 'pointer'
                  }}
                >
                  History ({history.length}) {showHistory ? '▲' : '▼'}
                </button>
                {showHistory && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '32px',
                      left: 0,
                      zIndex: 30,
                      background: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      width: '580px',
                      maxHeight: '240px',
                      overflowY: 'auto'
                    }}
                  >
                    {history.map((q, i) => (
                      <div
                        key={i}
                        onClick={() => {
                          this.handleSqlChange(q);
                          this.setState({ showHistory: false });
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f1f5f9'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f8fafc';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#fff';
                        }}
                      >
                        {this.render_badge(detect_statement_type(q))}
                        <span
                          style={{
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            color: '#475569',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {q}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            {sql.trim() && (
              <button
                onClick={() => this.handleSqlChange('')}
                style={{
                  background: 'none',
                  border: '1px solid #cbd5e1',
                  borderRadius: '5px',
                  padding: '4px 10px',
                  fontSize: '12px',
                  color: '#475569',
                  cursor: 'pointer'
                }}
              >
                Clear
              </button>
            )}
          </div>
          <span style={{ fontSize: '11px', color: '#94a3b8', letterSpacing: '0.04em' }}>
            SQL Editor
          </span>
        </div>

        {/* Monaco — dark theme */}
        <Editor
          height="260px"
          language="sql"
          value={sql}
          onChange={this.handleSqlChange}
          theme="vs-dark"
          options={{
            minimap: { enabled: false },
            fontSize: 14,
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            automaticLayout: true,
            padding: { top: 14, bottom: 14 },
            renderLineHighlight: 'line'
          }}
        />

        {/* Action row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 16px',
            background: '#f1f5f9',
            borderTop: '1px solid #e2e8f0',
            borderBottom: '1px solid #e2e8f0',
            gap: '12px'
          }}
        >
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={this.runDryRun}
              disabled={!sql.trim() || loadingDryRun}
              style={{
                padding: '7px 18px',
                background: !sql.trim() || loadingDryRun ? '#e2e8f0' : '#2563eb',
                color: !sql.trim() || loadingDryRun ? '#94a3b8' : '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: !sql.trim() || loadingDryRun ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '13px'
              }}
            >
              {loadingDryRun ? 'Running...' : '▷  Dry Run'}
            </button>
            <button
              onClick={this.runExecute}
              disabled={!canExec || loadingExecute}
              style={{
                padding: '7px 18px',
                background: !canExec || loadingExecute ? '#e2e8f0' : '#dc2626',
                color: !canExec || loadingExecute ? '#94a3b8' : '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: !canExec || loadingExecute ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '13px'
              }}
            >
              {loadingExecute ? 'Executing...' : '⚡  Execute'}
            </button>
          </div>

          {/* Inline status */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '12.5px',
              color: '#64748b'
            }}
          >
            {(dryRunError || executeError) && (
              <span style={{ color: '#dc2626', fontWeight: 500 }}>
                ✗ {dryRunError || executeError}
              </span>
            )}
            {!dryRunError && !executeError && resultStatement && (
              <>
                {this.render_badge(resultStatement)}
                <span>
                  {isExecuted
                    ? `${rowCount} ${resultStatement === 'SELECT' ? 'rows returned' : 'rows affected'}`
                    : DDL_TYPES.has(resultStatement)
                      ? 'Preview not available for DDL'
                      : `${rowCount} ${resultStatement === 'SELECT' ? 'rows returned' : 'rows would be affected'} · no data changed`}
                </span>
              </>
            )}
            {!dryRunDone && !loadingDryRun && sql.trim() && (
              <span style={{ color: '#94a3b8', fontSize: '12px' }}>Run Dry Run first</span>
            )}
          </div>
        </div>

        {/* Warning / confirm panel — only for high-impact or DDL */}
        {dryRunResult?.warning && (
          <div
            style={{
              padding: '12px 16px',
              background: DDL_TYPES.has(dryRunResult.statementType) ? '#fef2f2' : '#fffbeb',
              borderBottom: `1px solid ${DDL_TYPES.has(dryRunResult.statementType) ? '#fca5a5' : '#fcd34d'}`
            }}
          >
            <div
              style={{
                fontSize: '12.5px',
                fontWeight: 700,
                marginBottom: '8px',
                color: DDL_TYPES.has(dryRunResult.statementType) ? '#b91c1c' : '#92400e'
              }}
            >
              ⚠ {dryRunResult.warning}
            </div>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '12.5px',
                cursor: 'pointer',
                color: DDL_TYPES.has(dryRunResult.statementType) ? '#7f1d1d' : '#78350f'
              }}
            >
              <input
                type="checkbox"
                checked={highImpactConfirmed}
                onChange={(e) => this.setState({ highImpactConfirmed: e.target.checked })}
              />
              {DDL_TYPES.has(dryRunResult.statementType)
                ? 'I understand this DDL statement executes immediately and cannot be rolled back'
                : 'I understand this is a high-impact query and want to proceed'}
            </label>
          </div>
        )}

        {/* Results pane */}
        <div style={{ background: '#fff' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 16px',
              borderBottom: '1px solid #f1f5f9'
            }}
          >
            <span
              style={{
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                color: '#94a3b8',
                textTransform: 'uppercase'
              }}
            >
              Results
            </span>
            {rowCount !== null && (
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#64748b',
                  background: '#f1f5f9',
                  padding: '2px 8px',
                  borderRadius: '10px'
                }}
              >
                {rowCount} {rowCount === 1 ? 'row' : 'rows'}
              </span>
            )}
          </div>
          <div style={{ minHeight: '140px' }}>
            {displayRows ? (
              this.render_results_table(displayRows)
            ) : (
              <div
                style={{ padding: '40px', textAlign: 'center', color: '#cbd5e1', fontSize: '13px' }}
              >
                {dryRunDone ? 'No rows to preview.' : 'Run a query to see results here.'}
              </div>
            )}
          </div>
        </div>

        {/* Footer: rules + audit link */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 16px',
            background: '#f8fafc',
            borderTop: '1px solid #e2e8f0'
          }}
        >
          {!rulesDismissed ? (
            <span
              style={{
                fontSize: '11.5px',
                color: '#94a3b8',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              ℹ UPDATE/DELETE require WHERE · DDL executes immediately · Writes are audit-logged
              <button
                onClick={() => {
                  localStorage.setItem(RULES_DISMISSED_KEY, 'true');
                  this.setState({ rulesDismissed: true });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#cbd5e1',
                  cursor: 'pointer',
                  fontSize: '12px',
                  marginLeft: '2px',
                  padding: 0
                }}
              >
                ✕
              </button>
            </span>
          ) : (
            <span />
          )}
          <button
            onClick={() => this.switchTab('audit')}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '12px',
              color: '#64748b',
              cursor: 'pointer',
              fontWeight: 500
            }}
          >
            Audit Log →
          </button>
        </div>
      </>
    );
  };

  render_audit_view = () => {
    const { auditLogs, loadingAuditLogs, hasMoreAuditLogs } = this.state;

    return (
      <>
        {/* Audit header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            background: '#f8fafc',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={() => this.switchTab('editor')}
              style={{
                background: 'none',
                border: 'none',
                color: '#2563eb',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 500,
                padding: 0
              }}
            >
              ← Editor
            </button>
            <span style={{ color: '#e2e8f0' }}>|</span>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>Audit Log</span>
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>
              Most recent writes. SELECT is not logged.
            </span>
          </div>
          <button
            onClick={() => this.loadAuditLogs(true)}
            style={{
              background: 'none',
              border: '1px solid #e2e8f0',
              borderRadius: '5px',
              padding: '4px 12px',
              fontSize: '12px',
              color: '#64748b',
              cursor: 'pointer'
            }}
          >
            Refresh
          </button>
        </div>

        <div style={{ minHeight: '200px' }}>
          {auditLogs.length === 0 && !loadingAuditLogs && (
            <div
              style={{ padding: '48px', textAlign: 'center', color: '#cbd5e1', fontSize: '13px' }}
            >
              No audit log entries yet.
            </div>
          )}
          {auditLogs.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '12.5px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['When', 'User', 'Query', 'Rows'].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: '8px 12px',
                          textAlign: 'left',
                          fontWeight: 600,
                          color: '#64748b',
                          fontSize: '11px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          borderBottom: '2px solid #e2e8f0',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log, i) => (
                    <tr key={log.id} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                      <td
                        style={{
                          padding: '8px 12px',
                          color: '#64748b',
                          whiteSpace: 'nowrap',
                          borderBottom: '1px solid #f1f5f9'
                        }}
                        title={new Date(log.executed_at).toLocaleString()}
                      >
                        {relative_time(log.executed_at)}
                      </td>
                      <td
                        style={{
                          padding: '8px 12px',
                          fontFamily: 'monospace',
                          color: '#94a3b8',
                          borderBottom: '1px solid #f1f5f9'
                        }}
                        title={log.cms_user_uuid}
                      >
                        {log.cms_user_uuid.slice(0, 8)}…
                      </td>
                      <td
                        style={{
                          padding: '8px 12px',
                          maxWidth: '420px',
                          borderBottom: '1px solid #f1f5f9'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {this.render_badge(detect_statement_type(log.query_text))}
                          <span
                            style={{
                              fontFamily: 'monospace',
                              color: '#475569',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={log.query_text}
                          >
                            {log.query_text}
                          </span>
                        </div>
                      </td>
                      <td
                        style={{
                          padding: '8px 12px',
                          textAlign: 'center',
                          fontWeight: 700,
                          color: log.rows_affected > 50 ? '#dc2626' : '#475569',
                          borderBottom: '1px solid #f1f5f9'
                        }}
                      >
                        {log.rows_affected}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {hasMoreAuditLogs && auditLogs.length > 0 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9' }}>
            <button
              onClick={() => this.loadAuditLogs(false)}
              disabled={loadingAuditLogs}
              style={{
                background: 'none',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                padding: '6px 16px',
                fontSize: '12.5px',
                color: '#64748b',
                cursor: loadingAuditLogs ? 'not-allowed' : 'pointer'
              }}
            >
              {loadingAuditLogs ? 'Loading...' : 'Load more'}
            </button>
          </div>
        )}
      </>
    );
  };

  render() {
    const { activeTab, accessAllowed } = this.state;

    if (accessAllowed === null) {
      return (
        <div>
          <div style={{ padding: '80px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
            Checking access...
          </div>
        </div>
      );
    }

    if (!accessAllowed) {
      return (
        <div>
          <div
            style={{
              maxWidth: '420px',
              margin: '60px auto',
              padding: '32px',
              background: '#fff',
              border: '1px solid #fee2e2',
              borderRadius: '12px',
              textAlign: 'center',
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)'
            }}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: '#fee2e2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: '22px'
              }}
            >
              🔒
            </div>
            <div
              style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}
            >
              Access Denied
            </div>
            <div style={{ fontSize: '13.5px', color: '#64748b' }}>
              Query Studio requires SUPER_USER access. Contact your admin.
            </div>
          </div>
        </div>
      );
    }

    return (
      <div>
        <div style={{ maxWidth: '1020px', margin: '20px auto', padding: '0 16px 40px' }}>
          <div
            style={{
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
            }}
          >
            {/* Dark header bar */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 18px',
                background: '#0f172a'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span
                  style={{
                    color: '#f1f5f9',
                    fontSize: '13px',
                    fontWeight: 700,
                    letterSpacing: '0.02em'
                  }}
                >
                  Query Studio
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {(() => {
                  const stage = import.meta.env.VITE_APP_STAGE;
                  const isProd = stage === 'production';
                  return (
                    <span
                      style={{
                        background: isProd ? '#450a0a' : '#1c1917',
                        color: isProd ? '#f87171' : '#fb923c',
                        fontSize: '10px',
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        padding: '3px 8px',
                        borderRadius: '4px',
                        textTransform: 'uppercase'
                      }}
                    >
                      {isProd ? 'Production' : stage === 'staging' ? 'Staging' : 'Dev'}
                    </span>
                  );
                })()}
                <span
                  style={{
                    width: '7px',
                    height: '7px',
                    borderRadius: '50%',
                    background: '#22c55e',
                    display: 'inline-block'
                  }}
                />
                <span
                  style={{
                    background: '#1e293b',
                    color: '#94a3b8',
                    fontSize: '11px',
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    padding: '3px 8px',
                    borderRadius: '4px'
                  }}
                >
                  SUPER_USER
                </span>
              </div>
            </div>

            {activeTab === 'editor' ? this.render_editor_view() : this.render_audit_view()}
          </div>
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, msg) =>
    dispatch(actions.set_notification_variable(show, type, msg))
});

export default connect(null, mapDispatchToProps)(DbConsole);
